<template>
    <div :dir="$root.$i18n.locale == 'ar' ? 'rtl' : 'ltr'">
        <div class="tw-grid tw-grid-cols-12 tw-min-h-[100dvh] tw-relative">
            <div class="tw-relative tw-pb-24 tw-col-span-12 md:tw-col-span-7 lg:tw-col-span-5 2xl:tw-col-span-4 tw-h-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-flex-col tw-p-5">
                <form @submit="submit" class="tw-w-full lg:tw-max-w-[350px] tw-mx-auto" action="">
                    <div class="tw-w-full lg:tw-max-w-[350px]">
                        <AppLogo />
                    </div>

                    <h1 class="tw-mt-5 lg:tw-text-3xl tw-text-xl tw-w-full tw-text-start tw-font-bold tw-text-gray-800">
                        {{ $t('pages.request-reset-password.title') }} <span class="tw-text-primary-500">!</span>
                    </h1>
                    <p class="tw-mb-7 tw-mt-2 tw-text-sm tw-font-semibold">
                        {{ $t('pages.request-reset-password.subtitle') }}
                    </p>

                    <FormField
                        v-model="email"
                        input-dir="ltr"
                        type="email"
                        placeholder="example@email.com"
                        left-icon="solar:letter-bold-duotone"
                        :label="$t('fields.email')"
                        :error="errors.email && $t('errors.' + errors.email, { field: $t('fields.email') })"
                        :disabled="isSubmitting || requestSent"
                    />

                    <p v-if="successMessage" class="tw-text-xs tw-text-emerald-600 tw-mt-3">
                        {{ successMessage }}
                    </p>

                    <div class="tw-mb-7"></div>

                    <div v-if="!requestSent">
                        <a-button
                            intent="primary"
                            :loading="isSubmitting"
                            :disabled="isSubmitting"
                            full
                        >
                            {{ $t('pages.request-reset-password.submit') }}
                        </a-button>
                    </div>
                    <div v-else>
                        <router-link :to="{ name: 'auth.login' }">
                            <a-button intent="primary" full>
                                {{ $t('buttons.sign-in') }}
                            </a-button>
                        </router-link>
                    </div>

                    <div class="tw-flex tw-w-full tw-text-sm tw-justify-center tw-text-gray-700 tw-mt-3">
                        <p>
                            {{ $t('pages.request-reset-password.back-to-login-text') }}
                            <router-link
                                :to="{ name: 'auth.login' }"
                                type="button"
                                class="tw-text-primary-500 tw-px-1 tw-font-semibold tw-duration-200 hover:tw-text-primary-600"
                            >{{ $t('buttons.sign-in') }}</router-link>
                        </p>
                    </div>
                </form>

                <div class="tw-flex tw-items-center tw-justify-between tw-absolute tw-bottom-0 tw-p-5 tw-text-center tw-w-full">
                    <p class="tw-text-sm tw-text-gray-600">
                        {{ $t('pages.copyright', { year: (new Date()).getFullYear(), appName: APP_NAME }) }}
                    </p>
                    <AppLanguageSwitcher />
                </div>
            </div>

            <div class="tw-col-span-12 md:tw-col-span-5 lg:tw-col-span-7 2xl:tw-col-span-8 tw-relative tw-h-full tw-hidden md:tw-block">
                <img :src="$frontend('assets/images/backgrounds/login-bg.jpg')" class="tw-h-full tw-w-full tw-object-cover tw-bg-left">
                <div class="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-z-10 tw-bg-gradient-to-t tw-from-black/80 tw-via-primary-900/70 tw-to-primary-600/40 tw-flex tw-items-center tw-justify-center tw-p-8 lg:tw-p-12">
                    <div class="tw-text-center tw-text-white tw-max-w-lg">
                        <h2 class="tw-text-4xl lg:tw-text-5xl tw-font-bold tw-mb-4 tw-leading-tight tw-drop-shadow-lg tw-whitespace-nowrap">
                            {{ $t('pages.login.overlay-heading') }}
                        </h2>
                        <p class="tw-text-lg lg:tw-text-xl tw-font-medium tw-opacity-95 tw-leading-relaxed tw-drop-shadow-md">
                            {{ $t('pages.login.overlay-description') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import AppLogo from '@/components/AppLogo';
import AppLanguageSwitcher from '@/components/AppLanguageSwitcher';
import FormField from '@/components/form/FormField';
import { APP_NAME } from '@/config/app';
import { requestPasswordReset } from '@/api/auth';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

const email = ref('');
const isSubmitting = ref(false);
const successMessage = ref('');
const requestSent = ref(false);

const errors = reactive({
    email: '',
});

const submit = async ($e) => {
    $e.preventDefault();

    errors.email = '';
    successMessage.value = '';

    if (!email.value) {
        errors.email = 'required';
        return;
    }

    isSubmitting.value = true;

    try {
        await requestPasswordReset({ email: email.value });

        // Show generic success to avoid leaking whether the email exists
        const message = t('pages.request-reset-password.success-message');
        successMessage.value = message;
        useAlert(message, 'success');
        requestSent.value = true;
    } catch (e) {
        // Map backend validation or general error
        if (e?.response?.data?.errors?.email) {
            errors.email = e.response.data.errors.email[0] || 'something-went-wrong';
        } else {
            useAlert(t('errors.something-wrong-happened'), 'danger');
        }
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<style>
</style>


